/**
 * Task service — CRUD operations against Medplum.
 *
 * Each function accepts the Medplum client and returns the raw FHIR Task so
 * callers own the optimistic-update / rollback lifecycle.
 */

import type { MedplumClient } from '@medplum/core';
import type { Meta, Task, Reference } from '@medplum/fhirtypes';
import { isSystemManagedTask } from '@unify/fhir-model/resources/task';
import { getPatientAccountRefs } from '../../../lib/clientFacingTasks';
import { executeTaskMutation } from '../../../lib/taskLifecycleBots';
import type { TaskFormValues, TaskInboxStatus, TaskPriority } from '../types';
import {
  fhirStatusToUiStatus,
  mergeTaskUpdates,
  setTaskDueDate,
} from './taskFhirMapping';
import { notifyTaskAssignee } from '../../../lib/notificationLifecycle';

// ─── Fetch ─────────────────────────────────────────────────────────────

export interface SearchTasksParams {
  /** Practitioner reference for owner filter (e.g. "Practitioner/abc"). */
  ownerRef?: string;
  /** Maximum results to return. */
  count?: number;
}

/**
 * Fetch tasks visible to the current user.
 *
 * We fetch a bounded set and filter client-side for now.  Server-side
 * filtering should be added when volumes grow.
 */
export async function searchTasks(
  medplum: MedplumClient,
  params: SearchTasksParams = {},
): Promise<Task[]> {
  medplum.invalidateSearches('Task');

  const search: Record<string, string> = {
    _sort: '-_lastUpdated',
    _count: String(params.count ?? 200),
  };

  if (params.ownerRef) {
    search.owner = params.ownerRef;
  }

  return medplum.searchResources('Task', search);
}

/** Stamp CareTeam compartment refs from Patient.meta so %care_team policies match (see clientFacingTasks). */
async function withPatientCompartmentMeta(medplum: MedplumClient, task: Task): Promise<Task> {
  const ref = task.for?.reference;
  if (!ref?.startsWith('Patient/')) {
    return stripCompartmentAccountsFromMeta(task);
  }
  const patientId = ref.slice('Patient/'.length);
  const accounts = await getPatientAccountRefs(medplum, patientId);
  if (accounts.length === 0) {
    return stripCompartmentAccountsFromMeta(task);
  }
  return { ...task, meta: { ...task.meta, accounts } };
}

function stripCompartmentAccountsFromMeta(task: Task): Task {
  if (!task.meta) return task;
  const hasCompartment =
    (task.meta.accounts && task.meta.accounts.length > 0) || Boolean(task.meta.account);
  if (!hasCompartment) return task;
  const { accounts: _a, account: _acct, ...metaRest } = task.meta as Meta & {
    accounts?: Reference[];
    account?: Reference;
  };
  const nextMeta = Object.keys(metaRest).length > 0 ? (metaRest as Meta) : undefined;
  return { ...task, meta: nextMeta };
}

// ─── Update (full) ─────────────────────────────────────────────────────

/**
 * Raised when a multi-step update fails part-way. `serverTask` is the Task as the server now
 * holds it (earlier steps may have been applied), so callers can reconcile instead of rolling
 * back to a stale copy. Undefined only when the re-read itself failed.
 */
export class TaskUpdateError extends Error {
  constructor(
    readonly cause: unknown,
    readonly serverTask: Task | undefined,
  ) {
    super(cause instanceof Error ? cause.message : 'Task update failed');
    this.name = 'TaskUpdateError';
  }
}

export async function updateTask(
  medplum: MedplumClient,
  existing: Task,
  values: TaskFormValues,
): Promise<Task> {
  const assigneeChanged = (values.assignee?.reference ?? undefined) !== existing.owner?.reference;
  if (assigneeChanged) assertAssignee(values.assignee ?? undefined); // fail before any write

  // Status and owner have their own bot actions and update-payload rejects changes to either,
  // so run those first; the payload then carries the already-applied status and owner.
  let current = existing;
  try {
    if (values.status !== fhirStatusToUiStatus(existing.status)) {
      current = await updateTaskStatus(medplum, current, values.status);
    }
    if (assigneeChanged) {
      current = await updateTaskAssignee(medplum, current, values.assignee ?? undefined);
    }
    const merged: Task = {
      ...mergeTaskUpdates(current, values),
      status: current.status,
      owner: current.owner,
    };
    const stamped = await withPatientCompartmentMeta(medplum, merged);
    return await executeTaskMutation<Task>(medplum, { action: 'update-payload', task: stamped });
  } catch (err) {
    const serverTask = existing.id
      ? await medplum.readResource('Task', existing.id).catch(() => undefined)
      : undefined;
    throw new TaskUpdateError(err, serverTask);
  }
}

// ─── Patch helpers ─────────────────────────────────────────────────────

export async function updateTaskStatus(
  medplum: MedplumClient,
  task: Task,
  status: TaskInboxStatus,
): Promise<Task> {
  if (!task.id) throw new Error('Task id is required');
  if (status === 'completed' && isSystemManagedTask(task)) {
    throw new Error('System-managed tasks cannot be marked complete from the inbox');
  }
  if (status === 'cancelled' || status === 'completed') {
    await executeTaskMutation(medplum, {
      action: status === 'cancelled' ? 'cancel' : 'complete',
      taskId: task.id,
    });
    return { ...task, status };
  }
  return executeTaskMutation<Task>(medplum, { action: 'set-status', taskId: task.id, status });
}

export async function updateTaskPriority(
  medplum: MedplumClient,
  task: Task,
  priority: TaskPriority,
): Promise<Task> {
  const updated: Task = {
    ...task,
    priority,
    lastModified: new Date().toISOString(),
  };
  return executeTaskMutation<Task>(medplum, { action: 'update-payload', task: updated });
}

function assertAssignee(assignee: Reference | undefined): asserts assignee is Reference {
  if (!assignee?.reference) {
    throw new Error('Tasks cannot be unassigned; reassign to another owner instead.');
  }
}

export async function updateTaskAssignee(
  medplum: MedplumClient,
  task: Task,
  assignee: Reference | undefined,
): Promise<Task> {
  if (!task.id) throw new Error('Task id is required');
  assertAssignee(assignee);
  const saved = await executeTaskMutation<Task>(medplum, {
    action: 'reassign',
    taskId: task.id,
    ownerReference: assignee.reference,
  });
  void notifyTaskAssignee(medplum, saved, { title: 'Task reassigned' }).catch(() => undefined);
  return saved;
}

export async function updateTaskDueDate(
  medplum: MedplumClient,
  task: Task,
  dueDate: string | null,
): Promise<Task> {
  let updated: Task = { ...task, lastModified: new Date().toISOString() };
  updated = setTaskDueDate(updated, dueDate);
  return executeTaskMutation<Task>(medplum, { action: 'update-payload', task: updated });
}

// ─── Delete / Cancel ───────────────────────────────────────────────────
// We cancel tasks (set status=cancelled) rather than hard-deleting.

export async function cancelTask(
  medplum: MedplumClient,
  task: Task,
): Promise<Task> {
  return updateTaskStatus(medplum, task, 'cancelled');
}

export async function deleteTask(
  medplum: MedplumClient,
  task: Task,
): Promise<void> {
  if (!task.id) throw new Error('Task id is required');
  await executeTaskMutation(medplum, { action: 'cancel', taskId: task.id });
}

// ─── Bulk ──────────────────────────────────────────────────────────────

export interface BulkUpdateFields {
  status?: TaskInboxStatus;
  priority?: TaskPriority;
  assignee?: Reference;
}

export interface BulkResult {
  succeeded: Task[];
  failed: Array<{ task: Task; error: unknown }>;
}

export async function bulkUpdateTasks(
  medplum: MedplumClient,
  tasks: Task[],
  changes: BulkUpdateFields,
): Promise<BulkResult> {
  const results = await Promise.allSettled(
    tasks.map(async (task) => {
      let updated = task;
      if (changes.status != null) {
        updated = await updateTaskStatus(medplum, updated, changes.status);
      }
      if (changes.priority != null) {
        updated = await updateTaskPriority(medplum, updated, changes.priority);
      }
      if (changes.assignee != null) {
        updated = await updateTaskAssignee(medplum, updated, changes.assignee);
      }
      return updated;
    }),
  );

  const succeeded: Task[] = [];
  const failed: BulkResult['failed'] = [];

  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      succeeded.push(result.value);
    } else {
      failed.push({ task: tasks[i], error: result.reason });
    }
  });

  return { succeeded, failed };
}

export async function bulkCancelTasks(
  medplum: MedplumClient,
  tasks: Task[],
): Promise<BulkResult> {
  return bulkUpdateTasks(medplum, tasks, { status: 'cancelled' });
}
